import { StyleSheet } from "react-native-unistyles";
import { Banner, Button } from "@visioni/a3s-ui/components";
import {
  useRunPatriotConnectionCheck,
  type PatriotConnectionCheckStepResult,
  type RunPatriotConnectionCheckResponse,
} from "@visioni/a3s-monitoring/app";
import { Text, View } from "@src/components";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { connectionCheckFailureCopy } from "../connection-check-copy";

export function ConnectionCheckScreen() {
  const check = useRunPatriotConnectionCheck();
  const run = () => {
    check.mutate();
  };

  return (
    <PatriotToolsPage
      title="Connection check"
      subtitle="Checks that a3s can reach Patriot, sign in, and read each kind of record it copies. Use it when the other Patriot tools pages fail or look out of date."
      titleAccessory={
        <Button label="Run check" size="sm" onPress={run} loading={check.isPending} loadingText="Running…" />
      }
    >
      {check.isPending || check.error !== null ?
        <QueryNotice
          isLoading={check.isPending}
          error={check.error}
          onRetry={run}
          loadingLabel="Running the check…"
          errorTitle="Could not run the check"
        />
      : check.data === undefined ?
        <Text textStyle="sm/regular" color="textMuted">
          Not run yet. A run takes a few seconds.
        </Text>
      : <ConnectionCheckReport report={check.data} />}
    </PatriotToolsPage>
  );
}

function ConnectionCheckReport({ report }: { report: RunPatriotConnectionCheckResponse }) {
  return (
    <View gap="lg">
      {report.failedAt !== null ?
        <Banner variant="warning" {...connectionCheckFailureCopy(report.failedAt)} />
      : report.unproven.length > 0 ?
        <Banner
          variant="info"
          title="Passed, with steps unproven"
          message="Some steps couldn't be proven, because Patriot had no records of that kind or the check ran out of time. If this persists, tell engineering."
        />
      : <Banner variant="info" title="Passed" message="Every step passed." />}
      <View style={styles.card}>
        <View style={[styles.row, styles.headerRow]}>
          <Text textStyle="xs/semibold" color="textMuted" style={styles.status}>
            Status
          </Text>
          <Text textStyle="xs/semibold" color="textMuted" style={styles.step}>
            Step
          </Text>
          <Text textStyle="xs/semibold" color="textMuted" style={styles.duration}>
            Time
          </Text>
          <Text textStyle="xs/semibold" color="textMuted" style={styles.detail}>
            Detail
          </Text>
        </View>
        {report.steps.map((step) => (
          <StepRow key={step.step} step={step} unproven={report.unproven.includes(step.step)} />
        ))}
      </View>
      {/* The report as the API returned it, for when the summary above hides something. */}
      <View gap="sm">
        <Text textStyle="xs/semibold" color="textMuted">
          Raw response
        </Text>
        <View style={styles.raw}>
          <Text textStyle="xs/regular" color="text" style={styles.mono} selectable>
            {JSON.stringify(report, null, 2)}
          </Text>
        </View>
      </View>
    </View>
  );
}

function StepRow({ step, unproven }: { step: PatriotConnectionCheckStepResult; unproven: boolean }) {
  const [label, tone] =
    !step.ok ? (["Failed", styles.failed] as const)
    : unproven ? (["Unproven", styles.unproven] as const)
    : (["Passed", styles.passed] as const);

  return (
    <View style={styles.row}>
      <Text textStyle="xs/semibold" style={[styles.status, tone]}>
        {label}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.step}>
        {step.step}
      </Text>
      <Text textStyle="xs/regular" color="textMuted" style={styles.duration}>
        {`${String(step.durationMs)} ms`}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.detail}>
        {step.detail}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  status: { width: 80 },
  step: { width: 200 },
  duration: { width: 80 },
  detail: { flex: 1, minWidth: 200 },
  passed: { color: theme.colors.status.success },
  unproven: { color: theme.colors.status.warning },
  failed: { color: theme.colors.status.error },
  raw: {
    backgroundColor: theme.colors.surfaceCardInner,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  mono: { fontFamily: "monospace" },
}));
